import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { BusinessHoursTimeSelect } from "@/components/ui/compact-time-select";
import type { DaySchedule } from "../types";
import { createDefaultTimeSlot } from "../types";

interface OffPeakHoursSectionProps {
  schedules: DaySchedule[];
  onSchedulesChange: (schedules: DaySchedule[]) => void;
  disabled?: boolean;
}

interface DayRowProps {
  daySchedule: DaySchedule;
  onUpdate: (schedule: DaySchedule) => void;
  disabled?: boolean;
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function DayRow({ daySchedule, onUpdate, disabled = false }: DayRowProps) {
  const { t } = useTranslation("settings");

  const dayName = daySchedule.dayName ?? DAY_NAMES[daySchedule.dayOfWeek] ?? "Unknown";

  const handleAddSlot = () => {
    const newSlot = createDefaultTimeSlot("09:00", "17:00", daySchedule.timeSlots.length);
    onUpdate({
      ...daySchedule,
      timeSlots: [...daySchedule.timeSlots, newSlot],
    });
  };

  const handleRemoveSlot = (slotIndex: number) => {
    onUpdate({
      ...daySchedule,
      timeSlots: daySchedule.timeSlots.filter((_, i) => i !== slotIndex),
    });
  };

  const handleSlotChange = (slotIndex: number, field: "startTime" | "endTime", value: string) => {
    const updatedSlots = [...daySchedule.timeSlots];
    updatedSlots[slotIndex] = { ...updatedSlots[slotIndex], [field]: value };
    onUpdate({ ...daySchedule, timeSlots: updatedSlots });
  };

  const isValidTimeRange = (startTime: string, endTime: string): boolean => {
    return startTime < endTime;
  };

  const isClosed = daySchedule.timeSlots.length === 0;

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 py-2 border-b border-border/40 last:border-0">
      {/* Day name */}
      <div className="sm:w-24 sm:pt-2">
        <p className="font-medium text-sm">{t(`business.daysOfWeek.${dayName.toLowerCase()}`)}</p>
      </div>

      {/* Time slots */}
      <div className="flex-1 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2">
        {isClosed ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddSlot}
            disabled={disabled}
            className="h-8 px-2 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            {t("business.addHours")}
          </Button>
        ) : (
          <>
            {daySchedule.timeSlots.map((slot, slotIndex) => (
              <div
                key={slot.id}
                className="flex items-center gap-2 bg-accent/50 rounded-md px-3 py-1.5 w-full sm:w-auto"
              >
                <div className="flex items-center gap-1 flex-1 sm:flex-initial">
                  <BusinessHoursTimeSelect
                    value={slot.startTime}
                    onChange={(value) => handleSlotChange(slotIndex, "startTime", value)}
                    disabled={disabled}
                    size="sm"
                    className="flex-1 sm:flex-initial"
                  />
                  <span className="text-xs text-muted-foreground mx-1">-</span>
                  <BusinessHoursTimeSelect
                    value={slot.endTime}
                    onChange={(value) => handleSlotChange(slotIndex, "endTime", value)}
                    disabled={disabled}
                    size="sm"
                    className="flex-1 sm:flex-initial"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveSlot(slotIndex)}
                  disabled={disabled}
                  className="h-6 w-6 p-0 flex-shrink-0"
                >
                  <X className="h-3 w-3" />
                </Button>
                {!isValidTimeRange(slot.startTime, slot.endTime) && (
                  <span className="text-xs text-destructive flex-shrink-0">
                    {t("business.invalid")}
                  </span>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAddSlot}
              disabled={disabled}
              className="h-8 px-2 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              {t("business.addSlot")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function OffPeakHoursSection({
  schedules,
  onSchedulesChange,
  disabled = false,
}: OffPeakHoursSectionProps) {
  // Sort days to show Monday-Sunday
  const sortedSchedules = [...schedules].sort((a, b) => {
    const aSort = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
    const bSort = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
    return aSort - bSort;
  });

  const handleDayUpdate = (updatedSchedule: DaySchedule) => {
    const newSchedules = schedules.map((s) =>
      s.dayOfWeek === updatedSchedule.dayOfWeek ? updatedSchedule : s
    );
    onSchedulesChange(newSchedules);
  };

  return (
    <div className="space-y-2">
      <div className="divide-y divide-border/40 border rounded-md p-3">
        {sortedSchedules.map((schedule) => (
          <DayRow
            key={schedule.dayOfWeek}
            daySchedule={schedule}
            onUpdate={handleDayUpdate}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}
